import { useEffect, useState } from 'react';
import type { MaxsoldSettingsDto } from '@jordan/types';
import { Alert, Breadcrumb, Button, Input, PageHeader, SettingsPanel, SettingsRow, Spinner, Switch } from '@jordan/ui';
import { AppChrome } from '../components/AppChrome';
import { apiGet, apiPut } from '../lib/api';
import { MaxsoldPlaceSearch } from './maxsold/MaxsoldSearchMap';

export function MaxsoldSettingsPage() {
  const [cfg, setCfg] = useState<MaxsoldSettingsDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<MaxsoldSettingsDto>('/api/config/maxsold')
      .then(setCfg)
      .catch(() => setError('Failed to load Maxsold settings.'));
  }, []);

  const set = <K extends keyof MaxsoldSettingsDto>(k: K, v: MaxsoldSettingsDto[K]) =>
    setCfg((p) => (p ? { ...p, [k]: v } : p));

  const clearHome = () => setCfg((p) => (p ? { ...p, homeAddress: '', homeLat: null, homeLng: null } : p));

  const save = async () => {
    if (!cfg) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await apiPut<MaxsoldSettingsDto>('/api/config/maxsold', {
        homeAddress: cfg.homeAddress,
        homeLat: cfg.homeLat,
        homeLng: cfg.homeLng,
        surchargePercent: cfg.surchargePercent,
        bidsActiveOnlyDefault: cfg.bidsActiveOnlyDefault,
      });
      setCfg(saved);
      setMessage('Maxsold settings saved.');
    } catch {
      setError('Could not save — check the values and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppChrome>
      <PageHeader
        title={
          <>
            Configuration - <span className="fw-normal">Maxsold</span>
          </>
        }
        breadcrumb={
          <Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Configuration' }, { label: 'Maxsold' }]} />
        }
      />

      <div className="content">
        {message && (
          <Alert variant="success" icon="ph-check-circle" dismissible>
            {message}
          </Alert>
        )}
        {error && (
          <Alert variant="danger" icon="ph-warning" dismissible>
            {error}
          </Alert>
        )}

        {!cfg ? (
          <Spinner />
        ) : (
          <SettingsPanel
            title="Maxsold"
            description="Your Maxsold preferences. Account credentials are secrets and live in the environment (MAXSOLD_EMAIL / MAXSOLD_PASSWORD), not here."
          >
            <SettingsRow
              title="Home location"
              description="The default location used for auction search and any distance calculations. Search the address with Google and pick a match."
            >
              <div style={{ maxWidth: 520 }}>
                {cfg.homeAddress ? (
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <i className="ph-map-pin text-primary" />
                    <span className="fw-semibold">{cfg.homeAddress}</span>
                    <button type="button" className="btn btn-sm btn-light text-danger ms-1" onClick={clearHome}>
                      <i className="ph-x me-1" />
                      Clear
                    </button>
                  </div>
                ) : (
                  <div className="text-muted mb-2">No home location set — searches use the default map centre.</div>
                )}

                {cfg.mapsConfigured ? (
                  <MaxsoldPlaceSearch
                    apiKey={cfg.mapsApiKey}
                    placeholder="Search your home address…"
                    onPick={({ lat, lng, label }) =>
                      setCfg((p) => (p ? { ...p, homeAddress: label, homeLat: lat, homeLng: lng } : p))
                    }
                  />
                ) : (
                  <Alert variant="info" icon="ph-map-trifold">
                    The address picker needs a Google Maps key. Add <code>GOOGLE_MAPS_API_KEY</code> to the server{' '}
                    <code>.env</code> to enable it.
                  </Alert>
                )}

                {cfg.homeLat != null && cfg.homeLng != null && (
                  <div className="form-text">
                    Coordinates: {cfg.homeLat.toFixed(5)}, {cfg.homeLng.toFixed(5)}
                  </div>
                )}
              </div>
            </SettingsRow>

            <SettingsRow
              title="Surcharge (%)"
              description="Buyer's premium added to every bid. Used to show the true cost (bid + surcharge) on lots. Defaults to 18%."
            >
              <div style={{ maxWidth: 160 }}>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  value={cfg.surchargePercent}
                  onChange={(e) => set('surchargePercent', Number(e.target.value))}
                  hint="Percent added on top of each bid."
                />
              </div>
            </SettingsRow>

            <SettingsRow
              title="Bids: active lots only"
              description="When on, the Bids page opens showing only lots still active (hiding closed lots)."
            >
              <Switch
                label="Default to active lots only"
                checked={cfg.bidsActiveOnlyDefault}
                onChange={(e) => set('bidsActiveOnlyDefault', e.target.checked)}
              />
            </SettingsRow>

            <SettingsRow title="" description="">
              <Button variant="primary" disabled={busy} onClick={save}>
                Save changes
              </Button>
            </SettingsRow>
          </SettingsPanel>
        )}
      </div>
    </AppChrome>
  );
}
